import { ChangeEvent, useState } from 'react';
import { Box, Card, Title, Text } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { Domain, AiFeature } from '@tiendanube/common/src/enums';
import { Alert, Grid, InterfaceNameValue, Stack } from '@tiendanube/components';
import FeatureFlag from 'App/components/FeatureFlag';
import { AI_SMART_WEIGHT_DIMENSIONS_ENABLED } from 'App/featuresFlags';
import HelpLink from 'App/HelpLink';
import { InputNumberNimbus, useResponsive } from 'commons/components';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { HELP_LINK_WEIGHT_CALCULATION_URL } from 'commons/constants';
import { useTranslationLanguage } from 'commons/hooks';
import { useTrackFullCatalog } from 'domains/Catalog/hooks';
import {
  OnChangeToAllVariants,
  RequiredAiProperties,
} from 'domains/Catalog/Products/pages/components/Variants/types';
import {
  trackingProductDetailDepthBlur,
  trackingProductDetailHeightBlur,
  trackingProductDetailWidthBlur,
} from 'domains/Catalog/Products/tracking';
import { useProductSizeWithAI } from './hooks';
import ApplyValuesToAllVariants from '../ApplyValuesToAllVariants';
import WeighInput from '../WeightInput';

import './ProductSizes.scss';

export type SizeField = 'weight' | 'depth' | 'width' | 'height';
interface ProductSizesProps {
  appearance?: 'column' | 'row';
  weight: string;
  depth: string;
  width: string;
  height: string;
  showAlertNoWeightAndDimension: boolean;
  onChange: (data: InterfaceNameValue) => void;
  onChangeToAllVariants?: OnChangeToAllVariants;
  isEdit?: boolean;
  requiredAiProperties: RequiredAiProperties;
}

function ProductSizes({
  appearance = 'column',
  weight,
  depth,
  width,
  height,
  showAlertNoWeightAndDimension,
  onChange,
  onChangeToAllVariants,
  isEdit,
  requiredAiProperties,
}: Readonly<ProductSizesProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const language = useTranslationLanguage();
  const { isMobile } = useResponsive();
  const [isShowAlert, setIsShowAlert] = useState(false);
  const isRowAppearance = appearance === 'row' && !isMobile;
  const sender = useTrackFullCatalog();

  // AI functionality hook
  const {
    isGeneratingAI,
    handleFieldManualEdit,
    handleGenerateWithAI,
    hasExistingDimensions,
    isAIGeneratedField,
    getTooltipText,
  } = useProductSizeWithAI({
    weight,
    depth,
    width,
    height,
    onChange,
    requiredAiProperties,
  });

  const handleOnBlur = () => {
    setIsShowAlert(Number(weight) > 99);
  };

  const handleChange = ({
    target: { name, value },
  }: ChangeEvent<HTMLInputElement>) => {
    handleFieldManualEdit(name);
    onChange({ name, value });
  };

  const handleBlurDepth = () => {
    sender(() => {
      trackingProductDetailDepthBlur(isEdit);
    });
  };

  const handleBlurWidth = () => {
    sender(() => {
      trackingProductDetailWidthBlur(isEdit);
    });
  };

  const handleBlurHeight = () => {
    sender(() => {
      trackingProductDetailHeightBlur(isEdit);
    });
  };

  const fieldAppearance = showAlertNoWeightAndDimension ? 'warning' : 'neutral';

  const renderInputsWidthAndHeight = (
    <>
      <Grid.Column>
        <FormField label={t('products.detail.width')} id="width">
          <InputNumberNimbus
            aria-label={t('products.detail.width')}
            appearance={fieldAppearance}
            type="float"
            onChange={handleChange}
            append="cm"
            appendPosition="end"
            value={width}
            placeholder="30"
            name="width"
            onBlur={handleBlurWidth}
            aiGenerated={isAIGeneratedField('width')}
            disabled={isGeneratingAI}
          />
        </FormField>
        {onChangeToAllVariants && (
          <ApplyValuesToAllVariants
            value={width}
            onChange={onChangeToAllVariants('width', width)}
          />
        )}
      </Grid.Column>
      <Grid.Column>
        <FormField label={t('products.detail.height')} id="height">
          <InputNumberNimbus
            aria-label={t('products.detail.height')}
            appearance={fieldAppearance}
            type="float"
            onChange={handleChange}
            append="cm"
            appendPosition="end"
            value={height}
            placeholder="30"
            name="height"
            onBlur={handleBlurHeight}
            aiGenerated={isAIGeneratedField('height')}
            disabled={isGeneratingAI}
          />
        </FormField>
        {onChangeToAllVariants && (
          <ApplyValuesToAllVariants
            value={height}
            onChange={onChangeToAllVariants('height', height)}
          />
        )}
      </Grid.Column>
    </>
  );

  return (
    <Box data-style="stratus--product-sizes">
      <Card>
        <Card.Header>
          <Box display="flex" gap="2" alignItems="center">
            <Title as="h4">{t('products.detail.weightAndDimensions')}</Title>
            <FeatureFlag flag={AI_SMART_WEIGHT_DIMENSIONS_ENABLED}>
              <GenerateWithAiLink
                onClick={handleGenerateWithAI}
                trackingFeature={AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS}
                disabled={
                  !requiredAiProperties?.imageUrl ||
                  isGeneratingAI ||
                  hasExistingDimensions
                }
                tooltipText={getTooltipText()}
                tooltipMaxWidth="220px"
              />
            </FeatureFlag>
          </Box>
        </Card.Header>
        <Card.Body>
          <Stack wrap align="stretch" column>
            <Alert
              appearance="warning"
              show={isShowAlert}
              title={t('products.detail.alertWeightTitle')}
            >
              {t('products.detail.alertWeightDescription')}
            </Alert>
            <Stack.Item>
              <Text>{`${t('products.detail.infoWeightAndDimensions')}`}</Text>
            </Stack.Item>
            <Alert appearance="warning" show={showAlertNoWeightAndDimension}>
              {t('products.detail.alertNoWeightAndDimentions')}
            </Alert>
            <Stack.Item fill>
              <Grid>
                <Grid.Row>
                  <Grid.Column>
                    <WeighInput
                      appearance={fieldAppearance}
                      label={t('products.detail.weight')}
                      value={weight}
                      onChange={handleChange}
                      placeholder="0.14"
                      onBlur={handleOnBlur}
                      isEdit={isEdit}
                      aiGenerated={isAIGeneratedField('weight')}
                      disabled={isGeneratingAI}
                    />
                    {onChangeToAllVariants && (
                      <ApplyValuesToAllVariants
                        value={weight}
                        onChange={onChangeToAllVariants('weight', weight)}
                      />
                    )}
                  </Grid.Column>
                  <Grid.Column>
                    <FormField label={t('products.detail.depth')} id="depth">
                      <InputNumberNimbus
                        aria-label={t('products.detail.depth')}
                        appearance={fieldAppearance}
                        type="float"
                        onChange={handleChange}
                        append="cm"
                        appendPosition="end"
                        value={depth}
                        placeholder="30"
                        name="depth"
                        onBlur={handleBlurDepth}
                        aiGenerated={isAIGeneratedField('depth')}
                        disabled={isGeneratingAI}
                      />
                    </FormField>
                    {onChangeToAllVariants && (
                      <ApplyValuesToAllVariants
                        value={depth}
                        onChange={onChangeToAllVariants('depth', depth)}
                      />
                    )}
                  </Grid.Column>
                  {isRowAppearance && renderInputsWidthAndHeight}
                </Grid.Row>
                {!isRowAppearance && (
                  <Grid.Row>{renderInputsWidthAndHeight}</Grid.Row>
                )}
              </Grid>
            </Stack.Item>
          </Stack>
        </Card.Body>
        <Card.Footer>
          <HelpLink
            title={t('helpLink.weightCalculation')}
            linkURL={HELP_LINK_WEIGHT_CALCULATION_URL[language]}
            currentViewTracking={t('helpLink.weightCalculationTrackingName')}
            previousValue="product_details_weight_dimensions"
            appearance="neutral"
            textDecoration="underline"
            icon="external"
          />
        </Card.Footer>
      </Card>
    </Box>
  );
}

export default ProductSizes;
